import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["editor"]

  static values = {
    goalWords: { type: Number, default: -1 },
    wordCount: { type: Boolean, default: true },
    lines: { type: String, default: "lg" },
    minWords: { type: Number, default: 100000000 }
  }

  connect() {
    this.boundUpdateWordCount = this.updateWordCount.bind(this)
    this.boundConfigureEditor = this.configureEditor.bind(this)
    this.editorTarget.addEventListener("trix-initialize", this.boundConfigureEditor)

    if (this.editorTarget.editor) this.configureEditor()
  }

  disconnect() {
    this.editorTarget.removeEventListener("trix-initialize", this.boundConfigureEditor)
    this.editorTarget.removeEventListener("trix-change", this.boundUpdateWordCount)
    this.wordCountElement?.remove()
  }

  configureEditor() {
    if (this.editorConfigured || !this.editorTarget.toolbarElement) return

    this.editorConfigured = true
    this.editorTarget.classList.add(`trix-min-height-${this.linesValue}`)
    this.configureToolbar()

    if (this.wordCountValue) this.initializeWordCount()
  }

  configureToolbar() {
    const toolbar = this.editorTarget.toolbarElement
    if (!toolbar) return

    const unusedControls = [
      ".trix-button--icon-strike",
      ".trix-button--icon-link",
      ".trix-button--icon-quote",
      ".trix-button--icon-code",
      ".trix-button-group--file-tools"
    ]

    unusedControls.forEach((selector) => {
      toolbar.querySelectorAll(selector).forEach((control) => control.remove())
    })

    toolbar.querySelectorAll(".trix-button-group:empty").forEach((group) => group.remove())
  }

  initializeWordCount() {
    const toolbar = this.editorTarget.toolbarElement
    if (!toolbar) return

    this.wordCountElement = document.createElement("span")
    this.wordCountElement.className = "trix-word-count"
    this.wordCountElement.setAttribute("aria-live", "polite")
    this.wordCountElement.setAttribute("aria-label", "Word count")
    toolbar.appendChild(this.wordCountElement)

    this.editorTarget.addEventListener("trix-change", this.boundUpdateWordCount)
    this.updateWordCount()
  }

  updateWordCount() {
    if (!this.wordCountElement) return

    const count = this.calculateWordCount()
    this.wordCountElement.textContent = `${count} words`
    this.updateWordCountColour(count)
  }

  calculateWordCount() {
    const text = this.editorTarget.editor?.getDocument().toString() || this.editorTarget.textContent || ""
    return text.trim().split(/\s+/).filter(Boolean).length
  }

  updateWordCountColour(count) {
    const colourClasses = ["text-slate-500", "text-red-600", "text-amber-600", "text-green-700"]
    this.wordCountElement.classList.remove(...colourClasses)

    if (this.goalWordsValue <= 0) {
      this.wordCountElement.classList.add("text-slate-500")
      return
    }

    const ratio = count / this.goalWordsValue
    const colourClass = ratio < 0.5 ? "text-red-600" : ratio < 0.75 ? "text-amber-600" : "text-green-700"
    this.wordCountElement.classList.add(colourClass)
  }
}
